import { useRef, useState, useId } from "react";
import { insertQuoteRequest, insertShopOrder } from "@/lib/lead-insert";
import { attachmentContentType, safeAttachmentName, validateAttachments, ATTACHMENT_ACCEPT } from "@/lib/attachment-upload";
import { Send, AlertCircle, Loader2, Paperclip, X } from "lucide-react";
import { z } from "zod";
import { track } from "@/lib/analytics";
import { checkSubmission, honeypotInputProps } from "@/lib/spam-guard";
import { getSubmissionSource } from "@/lib/submission-source";
import { supabase } from "@/integrations/supabase/client";
import { openMailtoBackup } from "@/lib/open-mailto-backup";
import {
  CONTACT_METHODS,
  ContactMethodPicker,
  type ContactMethod,
  type ContactMethodValue,
} from "@/components/form-fields/ContactMethodPicker";

// Width 4'-30' in 1' increments
const WIDTHS = Array.from({ length: 27 }, (_, i) => `${i + 4}'`);
// Length 4'-40' in 1' increments
const LENGTHS = Array.from({ length: 37 }, (_, i) => `${i + 4}'`);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 8;

const schema = z.object({
  firstName: z.string().trim().min(1, "Please enter your first name").max(80),
  lastName: z.string().trim().min(1, "Please enter your last name").max(80),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone number")
    .max(25)
    .regex(/^[0-9+()\-.\s]+$/, "Phone can only contain numbers and ()-+. characters"),
  address: z.string().trim().min(5, "Please enter your address").max(300),
  width: z.string().min(1, "Choose a width"),
  length: z.string().min(1, "Choose a length"),
  contactMethod: z.enum(CONTACT_METHODS, { message: "Choose a preferred contact method" }),
  notes: z.string().trim().max(2000).optional(),
});

type Errors = Partial<Record<keyof z.infer<typeof schema>, string>>;

export function GazeboCoverForm() {
  const fid = useId();
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [contactMethod, setContactMethod] = useState<ContactMethodValue>("");
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const startedAt = useRef<number>(Date.now());

  const inputCls = (hasErr?: boolean) =>
    `w-full border bg-white px-4 py-3 text-sm focus:outline-none ${
      hasErr ? "border-red focus:border-red" : "border-input focus:border-navy-deep"
    }`;

  const FieldError = ({ msg }: { msg?: string }) =>
    msg ? (
      <p role="alert" className="mt-1 flex items-start gap-1 text-xs font-medium text-red">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>{msg}</span>
      </p>
    ) : null;

  const Select = ({
    id,
    label,
    value,
    onChange,
    options,
    placeholder,
    error,
  }: {
    id: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    options: readonly string[];
    placeholder: string;
    error?: string;
  }) => (
    <div>
      <label htmlFor={id} className="font-display text-xs uppercase tracking-wider text-navy-deep">
        {label}
      </label>
      <select
        id={id}
        name={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-2 ${inputCls(!!error)}`}
        aria-label={label}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <FieldError msg={error} />
    </div>
  );

  const onFilesPicked = (incoming: File[]) => {
    const merged = [...files];
    let err: string | null = null;
    for (const f of incoming) {
      if (f.size > MAX_FILE_SIZE) {
        err = `"${f.name}" is over 10 MB. Please attach a smaller file.`;
        continue;
      }
      if (merged.length >= MAX_FILES) {
        err = `You can attach up to ${MAX_FILES} files.`;
        break;
      }
      if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
    }
    setFiles(merged);
    setFileError(err);
    if (fileInput.current) fileInput.current.value = "";
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const raw = {
      firstName: String(fd.get("firstName") || ""),
      lastName: String(fd.get("lastName") || ""),
      email: String(fd.get("email") || ""),
      phone: String(fd.get("phone") || ""),
      address: String(fd.get("address") || ""),
      width,
      length,
      contactMethod,
      notes: String(fd.get("notes") || ""),
    };
    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof Errors;
        if (k && !next[k]) next[k] = issue.message;
      }
      setErrors(next);
      track("quote_form_submit", {
        target: "gazebo_cover",
        metadata: { status: "validation_error", fields: Object.keys(next).join(",") },
      });
      return;
    }
    setErrors({});
    const d = parsed.data;

    const guard = await checkSubmission({
      formId: "gazebo_cover",
      formData: fd,
      startedAt: startedAt.current,
      payload: { email: d.email, width: d.width, length: d.length },
    });
    if (!guard.ok) {
      if (guard.reason === "honeypot") return;
      setFormError(guard.message ?? "Please try again.");
      return;
    }
    setFormError(null);
    setSubmitting(true);

    type Uploaded = { url: string; path: string; name: string; type: string; size: number };
    let uploaded: Uploaded[] = [];
    const attachErr = validateAttachments(files);
    if (attachErr) {
      setFormError(attachErr);
      return;
    }
    if (files.length > 0) {
      try {
        const stamp = Date.now();
        const folder = `gazebo-cover/${crypto.randomUUID()}`;
        uploaded = await Promise.all(
          files.map(async (file, i) => {
            const safeName = safeAttachmentName(file);
            const path = `${folder}/${i}-${safeName}`;
            const { error } = await supabase.storage
              .from("contact-uploads")
              .upload(path, file, {
                contentType: attachmentContentType(file) ?? "image/jpeg",
                upsert: false,
              });
            if (error) throw error;
            const { data: signed } = await supabase.storage
              .from("contact-uploads")
              .createSignedUrl(path, 60 * 60 * 24 * 365);
            return {
              url: signed?.signedUrl ?? path,
              path,
              name: file.name,
              type: attachmentContentType(file) ?? "image/jpeg",
              size: file.size,
            };
          }),
        );
      } catch {
        setSubmitting(false);
        setFormError("We couldn't upload one of your photos. Please try again or email them directly.");
        return;
      }
    }
    const uploadedUrls = uploaded.map((u) => u.url);

    const fullName = `${d.firstName} ${d.lastName}`.trim();
    const optionsSummary = [
      `Width: ${d.width}`,
      `Length: ${d.length}`,
      `Preferred Contact: ${d.contactMethod}`,
    ].join(" · ");
    const attachmentsBlock =
      uploadedUrls.length > 0
        ? `\n\nAttached Photos (${uploadedUrls.length}):\n${uploadedUrls.join("\n")}`
        : "";
    const fullNotes =
      `${optionsSummary}\n\n` +
      (d.notes ? `Customer notes:\n${d.notes}` : "(no additional notes)") +
      attachmentsBlock;

    const source = getSubmissionSource("gazebo_cover_form");
    const { id: insertedId, error } = await insertQuoteRequest({
        product_id: "gazebo-cover",
        product_name: "Custom Gazebo / Pergola Cover",
        customer_name: fullName,
        customer_email: d.email,
        customer_phone: d.phone,
        shipping_address: d.address,
        size_option: `Width: ${d.width} · Length: ${d.length}`,
        fabric_option: null,
        color_option: null,
        quantity: 1,
        notes: fullNotes,
        ...source,
      });

    if (error || !insertedId) {
      setSubmitting(false);
      setFormError("We couldn't submit your request. Please call +1 (830) 390-9588 or try again.");
      return;
    }

    if (uploaded.length > 0) {
      await supabase.from("custom_quote_attachments").insert(
        uploaded.map((u, i) => ({
          quote_request_id: insertedId,
          file_url: u.url,
          storage_path: u.path,
          file_name: u.name,
          content_type: u.type,
          size_bytes: u.size,
          sort_order: i,
        })),
      );
    }

    setSubmitting(false);

    track("quote_form_submit", {
      target: "gazebo_cover",
      metadata: { status: "success", width: d.width, length: d.length },
    });
    openMailtoBackup({
      subject: `Custom Gazebo / Pergola Cover — ${fullName}`,
      body: `Name: ${fullName}\nEmail: ${d.email}\nPhone: ${d.phone}\nAddress: ${d.address}\n\n${fullNotes}`,
    });
    setSubmitted(true);
  };

  return (
    <section
      id="gazebo-cover-quote"
      className="scroll-mt-24 border-t-4 border-navy-deep bg-cream py-12 sm:py-20"
    >
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="grid gap-8 md:grid-cols-5 md:gap-10">
          <div className="md:col-span-2">
            <div className="font-display text-sm tracking-[0.3em] text-navy-deep">
              ★ GAZEBO COVER QUOTE
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep sm:text-4xl lg:text-5xl">
              Request a Custom Gazebo Cover Quote
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Gazebo tops, pergola covers and privacy panels — patterned and sewn in our Seguin shop.
              Send your dimensions and a photo of the structure and we'll send a written estimate.
            </p>
            <div className="mt-6 border-l-4 border-red bg-white p-4">
              <p className="font-display text-xs font-bold uppercase tracking-wider text-red">
                ★ Texas Only — Seguin Shop
              </p>
              <p className="mt-1 text-sm text-navy-deep">
                We pattern, build, and install gazebo and pergola covers across Texas — Seguin,
                San Antonio, New Braunfels, Austin, the Hill Country, and South Texas.
              </p>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Questions?{" "}
              <a href="tel:+18303909588" className="font-semibold text-navy-deep underline">
                +1 (830) 390-9588
              </a>
              <br />
              Email:{" "}
              <a href="mailto:office@texasmadeshade.com" className="font-semibold text-navy-deep underline">
                office@texasmadeshade.com
              </a>
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            noValidate
            className="md:col-span-3 space-y-5 border-l-4 border-navy-deep bg-white p-6 shadow-card md:p-8"
          >
            <input {...honeypotInputProps()} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <input
                  name="firstName"
                  placeholder="First Name"
                  className={inputCls(!!errors.firstName)}
                  aria-label="First Name"
                  maxLength={80}
                />
                <FieldError msg={errors.firstName} />
              </div>
              <div>
                <input
                  name="lastName"
                  placeholder="Last Name"
                  className={inputCls(!!errors.lastName)}
                  aria-label="Last Name"
                  maxLength={80}
                />
                <FieldError msg={errors.lastName} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <input
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  className={inputCls(!!errors.email)}
                  aria-label="Email Address"
                  maxLength={255}
                />
                <FieldError msg={errors.email} />
              </div>
              <div>
                <input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  className={inputCls(!!errors.phone)}
                  aria-label="Phone Number"
                  maxLength={25}
                />
                <FieldError msg={errors.phone} />
              </div>
            </div>
            <div>
              <input
                name="address"
                placeholder="Install Address (Texas only)"
                className={inputCls(!!errors.address)}
                aria-label="Install Address"
                maxLength={300}
              />
              <FieldError msg={errors.address} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id={`${fid}-width`}
                label="How Wide?"
                value={width}
                onChange={setWidth}
                options={WIDTHS}
                placeholder="Choose width…"
                error={errors.width}
              />
              <Select
                id={`${fid}-length`}
                label="How Long?"
                value={length}
                onChange={setLength}
                options={LENGTHS}
                placeholder="Choose length…"
                error={errors.length}
              />
            </div>

            <ContactMethodPicker
              value={contactMethod}
              onChange={(m) => {
                setContactMethod(m);
                setErrors((prev) => ({ ...prev, contactMethod: undefined }));
              }}
              error={errors.contactMethod}
            />

            <div>
              <textarea
                name="notes"
                rows={4}
                placeholder="Optional — frame material/color, # of sides for privacy panels, mounting notes, deadline, etc."
                className={inputCls(false)}
                aria-label="Notes"
                maxLength={2000}
              />
            </div>

            <div>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="hidden"
                onChange={(e) => onFilesPicked(Array.from(e.target.files ?? []))}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="inline-flex items-center gap-2 border-2 border-navy-deep bg-white px-5 py-3 font-display text-xs uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                >
                  <Paperclip className="h-4 w-4" /> Attach Photos
                  {files.length > 0 && (
                    <span className="ml-1 rounded-full bg-navy-deep px-2 py-0.5 text-xs text-white">
                      {files.length}
                    </span>
                  )}
                </button>
                <button
                  type="submit"
                  disabled={!contactMethod || submitting}
                  className="inline-flex items-center gap-2 bg-navy-deep px-7 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-navy disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-4 w-4" /> Request Estimate</>
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Up to {MAX_FILES} photos or PDFs (max 10 MB each) — pictures of your gazebo or
                pergola help us quote accurately.
              </p>
              {fileError && (
                <p role="alert" className="mt-1 text-xs font-medium text-red">{fileError}</p>
              )}
              {files.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                  {files.map((f, idx) => (
                    <li
                      key={`${f.name}-${idx}`}
                      className="flex items-center justify-between gap-2 border border-navy-deep/10 bg-cream/50 px-2 py-1"
                    >
                      <span className="truncate">
                        📎 {f.name}{" "}
                        <span className="text-navy-deep/70">
                          ({(f.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                        aria-label={`Remove ${f.name}`}
                        className="shrink-0 text-navy-deep/60 transition hover:text-red"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {formError && (
              <p role="alert" className="text-sm font-medium text-red">{formError}</p>
            )}
            {submitted && Object.keys(errors).length === 0 && (
              <div
                role="status"
                className="border-l-4 border-navy-deep bg-cream p-4 text-sm text-navy-deep"
              >
                ✓ Got it — we'll be in touch within 1 business day with your gazebo cover estimate.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
